import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search,
  Briefcase,
  Clock,
  GraduationCap,
  Building,
  Loader,
  User,
  MapPin,
  Linkedin,
  Github,
  Twitter,
  ChevronDown,
  Users,
  Filter,
  CheckCircle,
  ArrowUpRight,
  Phone,
  ChartBar,
} from "lucide-react";
import AlumniMap from "../components/AlumniMap";
import AnalyticsModal from "../components/AnalyticsModal";
import HeroSection from "../components/HeroSection";
// Utility functions
const getUniqueValues = (array, key) => {
  if (!array?.length) return [];
  const values = array.reduce((acc, item) => {
    const value = key.split(".").reduce((obj, k) => obj?.[k], item);
    if (value) acc.add(value);
    return acc;
  }, new Set());
  return [...values].sort();
};

const getExperienceLocations = (alumni) => {
  const locations = new Set();
  alumni.forEach((alumnus) => {
    alumnus.professionalInfo?.experiences?.forEach((exp) => {
      if (exp.location) locations.add(exp.location);
    });
  });
  return [...locations].sort();
};

const getCurrentCompanies = (alumni) => {
  return [
    ...new Set(
      alumni
        .filter((a) =>
          a.professionalInfo?.experiences?.some((e) => e.isCurrent)
        )
        .map(
          (a) =>
            a.professionalInfo.experiences.find((e) => e.isCurrent)?.company
        )
        .filter(Boolean)
    ),
  ].sort();
};

const getAllSkills = (alumni) => {
  const skills = new Set();
  alumni.forEach((a) => {
    a.professionalInfo?.skills?.forEach((skill) =>
      skills.add(skill.toLowerCase())
    );
  });
  return [...skills].sort();
};

// Component Definitions
const SearchBar = ({ value, onChange, className }) => (
  <div className={`relative ${className}`}>
    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
      <Search className="h-5 w-5 text-gray-400" />
    </div>
    <input
      type="text"
      className="w-full pl-12 pr-4 py-4 bg-white/90 backdrop-blur-lg rounded-2xl 
        shadow-lg focus:ring-2 focus:ring-blue-500 border-none text-gray-900 
        placeholder-gray-500 text-lg transition-all duration-200"
      placeholder="Search alumni by name, skills, company..."
      value={value}
      onChange={onChange}
    />
    <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
      <kbd className="hidden sm:inline-block px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 rounded">
        ⌘K
      </kbd>
    </div>
  </div>
);

const FilterSection = ({ label, children }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    {children}
  </div>
);

const SelectFilter = ({ value, onChange, options, placeholder }) => (
  <select
    className="w-full py-2.5 pl-3 pr-10 text-gray-900 bg-white border border-gray-300 
      rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent
      cursor-pointer transition-all duration-200"
    value={value}
    onChange={onChange}
  >
    <option value="">{placeholder}</option>
    {options.map((opt) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>
);

const Stat = ({ icon: Icon, value, label }) => (
  <div className="bg-white rounded-2xl shadow-sm p-6 flex items-center space-x-4 hover:shadow-md transition-shadow">
    <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
      <Icon className="h-6 w-6 text-blue-600" />
    </div>
    <div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  </div>
);

// Main Component
const AlumniDirectory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [alumni, setAlumni] = useState([]);
  const [filteredAlumni, setFilteredAlumni] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const [sortBy, setSortBy] = useState("recent");
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Complex filters based on API response
  const [filters, setFilters] = useState({
    search: "",
    department: "",
    batch: "",
    employmentStatus: "",
    currentCompany: "",
    location: "",
    skills: [],
    gender: "",
    degree: "",
    verificationStatus: "",
  });

  // Update the useEffect to depend on currentUserId
  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUserId !== null) {
      // Only fetch alumni after we have currentUserId
      fetchAlumni();
    }
  }, [currentUserId]);

  const fetchAlumni = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/alumni/directory`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch alumni");
      const data = await response.json();

      // Filter out current user from alumni list
      const filteredData = data.filter(
        (alumnus) => alumnus._id !== currentUserId
      );

      setAlumni(filteredData);
      setFilteredAlumni(filteredData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/alumni/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch current user");
      const data = await response.json();
      setCurrentUserId(data._id);
    } catch (err) {
      console.error("Error fetching current user:", err);
    }
  };
  useEffect(() => {
    applyFilters();
  }, [filters, sortBy]);
  const applyFilters = () => {
    let filtered = [...alumni];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (alumnus) =>
          alumnus.firstName.toLowerCase().includes(searchLower) ||
          alumnus.lastName.toLowerCase().includes(searchLower) ||
          alumnus.academicInfo?.department
            ?.toLowerCase()
            .includes(searchLower) ||
          alumnus.academicInfo?.degree?.toLowerCase().includes(searchLower) ||
          alumnus.enrollmentNumber?.toLowerCase().includes(searchLower) ||
          alumnus.professionalInfo?.skills?.some((skill) =>
            skill.toLowerCase().includes(searchLower)
          ) ||
          alumnus.professionalInfo?.experiences?.some(
            (exp) =>
              exp.company?.toLowerCase().includes(searchLower) ||
              exp.title?.toLowerCase().includes(searchLower)
          )
      );
    }

    // Department filter
    if (filters.department) {
      filtered = filtered.filter(
        (alumnus) => alumnus.academicInfo?.department === filters.department
      );
    }

    // Degree filter
    if (filters.degree) {
      filtered = filtered.filter(
        (alumnus) => alumnus.academicInfo?.degree === filters.degree
      );
    }
    // Batch filter
    if (filters.batch) {
      filtered = filtered.filter(
        (alumnus) => alumnus.graduationYear.toString() === filters.batch
      );
    }

    // Employment Status filter
    if (filters.employmentStatus) {
      filtered = filtered.filter((alumnus) => {
        if (filters.employmentStatus === "employed") {
          return alumnus.professionalInfo?.currentlyEmployed === true;
        } else {
          return alumnus.professionalInfo?.currentlyEmployed === false;
        }
      });
    }

    // Current Company filter
    if (filters.currentCompany) {
      filtered = filtered.filter((alumnus) =>
        alumnus.professionalInfo?.experiences?.some(
          (exp) => exp.isCurrent && exp.company === filters.currentCompany
        )
      );
    }

    // Location filter
    if (filters.location) {
      filtered = filtered.filter((alumnus) =>
        alumnus.professionalInfo?.experiences?.some(
          (exp) => exp.location === filters.location
        )
      );
    }

    // Skills filter
    if (filters.skills.length > 0) {
      filtered = filtered.filter((alumnus) =>
        filters.skills.every((skill) =>
          alumnus.professionalInfo?.skills
            ?.map((s) => s.toLowerCase())
            .includes(skill.toLowerCase())
        )
      );
    }

    // Gender filter
    if (filters.gender) {
      filtered = filtered.filter(
        (alumnus) => alumnus.personalInfo?.gender === filters.gender
      );
    }

    // Degree filter
    if (filters.degree) {
      filtered = filtered.filter(
        (alumnus) => alumnus.academicInfo?.degree === filters.degree
      );
    }

    // Verification Status filter
    if (filters.verificationStatus) {
      filtered = filtered.filter(
        (alumnus) =>
          alumnus.isVerified === (filters.verificationStatus === "verified")
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "recent":
          return new Date(b.createdAt) - new Date(a.createdAt);
        case "name":
          return `${a.firstName} ${a.lastName}`.localeCompare(
            `${b.firstName} ${b.lastName}`
          );
        case "batch":
          return b.graduationYear - a.graduationYear;
        case "verification":
          return (b.isVerified ? 1 : 0) - (a.isVerified ? 1 : 0);
        default:
          return 0;
      }
    });

    setFilteredAlumni(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Calculate statistics
  const stats = {
    totalAlumni: alumni.length,
    verifiedAlumni: alumni.filter((a) => a.isVerified).length,
    employedAlumni: alumni.filter((a) => a.professionalInfo?.currentlyEmployed)
      .length,
    totalCompanies: getCurrentCompanies(alumni).length,
    locations: [
      ...new Set([
        ...alumni
          .filter((a) => a.personalInfo?.currentLocation?.address)
          .map((a) => a.personalInfo.currentLocation.address.split(",")[0]),
        ...alumni.flatMap(
          (a) =>
            a.professionalInfo?.experiences?.map((exp) => exp.location) || []
        ),
      ]),
    ].filter(Boolean).length,
    departments: getUniqueValues(alumni, "department").length,
  };

  // Get unique values for filters
  const departments = getUniqueValues(alumni, "academicInfo.department");
  const batches = getUniqueValues(alumni, "graduationYear");
  const companies = getCurrentCompanies(alumni);
  const locations = getExperienceLocations(alumni);
  const skills = getAllSkills(alumni);
  const degrees = getUniqueValues(alumni, "academicInfo.degree");

  // Pagination
  const indexOfLastAlumni = currentPage * itemsPerPage;
  const indexOfFirstAlumni = indexOfLastAlumni - itemsPerPage;
  const currentPageAlumni = filteredAlumni.slice(
    indexOfFirstAlumni,
    indexOfLastAlumni
  );
  const totalPages = Math.ceil(filteredAlumni.length / itemsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <Loader className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-gray-600">Loading alumni directory...</p>
        </div>
      </div>
    );
  }

  // Get recently joined alumni (last 30 days)
  const recentlyJoined = alumni
    .filter((a) => {
      const joinDate = new Date(a.createdAt);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return joinDate > thirtyDaysAgo && a._id !== currentUserId;
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}

      <HeroSection
        title="Alumni Directory"
        subtitle={`Connect with our growing community of ${alumni.length.toLocaleString()}+ alumni across ${
          stats.locations
        } cities. `}
        showSearch={true}
        searchConfig={{
          value: filters.search,
          onChange: (e) => setFilters({ ...filters, search: e.target.value }),
          placeholder: "Search alumni by name, skills, company...",
        }}
        statsConfig={[
          { value: stats.totalAlumni.toLocaleString(), label: "Total Alumni" },
          { value: stats.totalCompanies.toLocaleString(), label: "Companies" },
          { value: stats.locations.toLocaleString(), label: "Cities" },
        ]}
        analyticsComponent={
          <AnalyticsModal
            isOpen={isAnalyticsOpen}
            onClose={() => setIsAnalyticsOpen(false)}
            alumni={alumni}
          />
        }
      />
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <Stat
            icon={Users}
            value={stats.totalAlumni.toLocaleString()}
            label="Total Alumni"
          />
        
          <Stat
            icon={Briefcase}
            value={stats.employedAlumni.toLocaleString()}
            label="Currently Employed"
          />
          <Stat
            icon={Building}
            value={stats.totalCompanies.toLocaleString()}
            label="Different Companies"
          />
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          >
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Advanced Filters
              </h2> 
            </div>
            <div className="flex items-center space-x-4">
              {isFiltersExpanded && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFilters({
                      search: "",
                      department: "",
                      batch: "",
                      employmentStatus: "",
                      currentCompany: "",
                      location: "",
                      skills: [],
                      gender: "",
                      degree: "",
                      verificationStatus: "",
                    });
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear all filters
                </button>
              )}
              <ChevronDown
                className={`h-5 w-5 text-gray-400 transition-transform duration-200
          ${isFiltersExpanded ? "transform rotate-180" : ""}`}
              />
            </div>
          </div>

          <motion.div
            initial={false}
            animate={{
              height: isFiltersExpanded ? "auto" : 0,
              opacity: isFiltersExpanded ? 1 : 0,
            }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
              {/* Previous filter sections remain the same */}
              <FilterSection label="Department">
                <SelectFilter
                  value={filters.department}
                  onChange={(e) =>
                    setFilters({ ...filters, department: e.target.value })
                  }
                  options={departments}
                  placeholder="All Departments"
                />
              </FilterSection>

              <FilterSection label="Graduation Year">
                <SelectFilter
                  value={filters.batch}
                  onChange={(e) =>
                    setFilters({ ...filters, batch: e.target.value })
                  }
                  options={batches}
                  placeholder="All Batches"
                />
              </FilterSection>

              <FilterSection label="Current Company">
                <SelectFilter
                  value={filters.currentCompany}
                  onChange={(e) =>
                    setFilters({ ...filters, currentCompany: e.target.value })
                  }
                  options={companies}
                  placeholder="All Companies"
                />
              </FilterSection>

              <FilterSection label="Location">
                <SelectFilter
                  value={filters.location}
                  onChange={(e) =>
                    setFilters({ ...filters, location: e.target.value })
                  }
                  options={locations}
                  placeholder="All Locations"
                />
              </FilterSection>

              <FilterSection label="Employment Status">
                <SelectFilter
                  value={filters.employmentStatus}
                  onChange={(e) =>
                    setFilters({ ...filters, employmentStatus: e.target.value })
                  }
                  options={["employed", "not employed"]}
                  placeholder="All Statuses"
                />
              </FilterSection>

              <FilterSection label="Degree">
                <SelectFilter
                  value={filters.degree}
                  onChange={(e) =>
                    setFilters({ ...filters, degree: e.target.value })
                  }
                  options={degrees}
                  placeholder="All Degrees"
                />
              </FilterSection>
            </div>
          </motion.div>
        </div>

        {/* Results and Sort Section */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-600">
            Showing <span className="font-medium">{filteredAlumni.length}</span>{" "}
            alumni
          </p>

          <div className="flex items-center space-x-4">
            <label className="text-sm text-gray-600">Sort by</label>
            <select
              className="text-sm border border-gray-300 rounded-lg px-3 py-2
              focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="recent">Most Recent</option>
              <option value="name">Name</option>
              <option value="batch">Graduation Year</option>
              <option value="verification">Verification Status</option>
            </select>
          </div>
        </div>

        {/* Alumni Grid */}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {currentPageAlumni.map((alumnus) => (
            <div
              key={alumnus._id}
              className="group bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all 
                duration-300 overflow-hidden cursor-pointer border border-gray-100"
              onClick={() => navigate(`/alumni/${alumnus._id}`)}
            >
              <div className="p-6">
                {/* Profile Header */}

                <div className="flex items-start space-x-4">
                  <div className="relative">
                    <div
                      className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 
                      flex items-center justify-center border-2 border-white shadow-md overflow-hidden"
                    >
                      {alumnus.personalInfo?.avatar ? (
                        <img
                          src={alumnus.personalInfo.avatar}
                          alt={`${alumnus.firstName} ${alumnus.lastName}`}
                          className="h-16 w-16 object-cover"
                        />
                      ) : (
                        <User className="h-8 w-8 text-blue-600" />
                      )}
                    </div>
                    {alumnus.isVerified && (
                      <div className="absolute -bottom-1 -right-1 bg-green-500 text-white p-1 rounded-full">
                        <CheckCircle className="h-3 w-3" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 truncate group-hover:text-blue-600">
                        {alumnus.firstName} {alumnus.lastName}
                      </h3>
                      <div className="flex items-center space-x-2">
                        {alumnus.socialLinks?.linkedin && (
                          <a
                            href={alumnus.socialLinks.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-gray-400 hover:text-blue-600 transition-colors"
                          >
                            <Linkedin className="h-4 w-4" />
                          </a>
                        )}
                        {alumnus.socialLinks?.github && (
                          <a
                            href={alumnus.socialLinks.github}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-gray-400 hover:text-gray-900 transition-colors"
                          >
                            <Github className="h-4 w-4" />
                          </a>
                        )}
                        {alumnus.socialLinks?.twitter && (
                          <a
                            href={alumnus.socialLinks.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-gray-400 hover:text-blue-400 transition-colors"
                          >
                            <Twitter className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>

                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-600">
                      <GraduationCap className="h-4 w-4" />
                      <span>Class of {alumnus.graduationYear}</span>
                      <span>•</span>
                      <span>{alumnus.academicInfo?.degree}</span>
                      <span>•</span>
                      <span>{alumnus.academicInfo?.department}</span>
                    </div>
                  </div>
                </div>

                {/* Current Role */}
                {alumnus.professionalInfo?.experiences?.find(
                  (exp) => exp.isCurrent
                ) && (
                  <div className="mt-4 flex items-center space-x-2 text-sm">
                    <Briefcase className="h-4 w-4 text-gray-400" />
                    <div>
                      <span className="font-medium text-gray-900">
                        {
                          alumnus.professionalInfo.experiences.find(
                            (exp) => exp.isCurrent
                          ).title
                        }
                      </span>
                      <span className="text-gray-600">
                        {" at "}
                        {
                          alumnus.professionalInfo.experiences.find(
                            (exp) => exp.isCurrent
                          ).company
                        }
                      </span>
                    </div>
                  </div>
                )}

                {/* Location */}
                {alumnus.professionalInfo?.experiences?.find(
                  (exp) => exp.isCurrent
                )?.location && (
                  <div className="mt-2 flex items-center space-x-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span>
                      {
                        alumnus.professionalInfo.experiences.find(
                          (exp) => exp.isCurrent
                        ).location
                      }
                    </span>
                  </div>
                )}

                {/* Skills */}
                {alumnus.professionalInfo?.skills?.length > 0 && (
                  <div className="mt-4">
                    <div className="flex flex-wrap gap-2">
                      {alumnus.professionalInfo.skills
                        .slice(0, 3)
                        .map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs 
                            font-medium bg-blue-50 text-blue-700"
                          >
                            {skill}
                          </span>
                        ))}
                      {alumnus.professionalInfo.skills.length > 3 && (
                        <span
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs 
                          font-medium bg-gray-100 text-gray-600"
                        >
                          +{alumnus.professionalInfo.skills.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Contact Actions */}
                <div className="mt-6 flex items-center justify-between">
                  {alumnus.personalInfo?.contactNumber && (
                    <a
                      href={`tel:${alumnus.personalInfo.contactNumber}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium 
                        text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      Contact
                    </a>
                  )}

                  <button
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium 
                      text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    View Profile
                    <ArrowUpRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredAlumni.length === 0 && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
              <Search className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No alumni found
            </h3>
            <p className="text-gray-500">
              Try adjusting your filters or search terms
            </p>
          </div>
        )}

        {/* Pagination */}
        {filteredAlumni.length > 0 && (
          <div className="flex items-center justify-center space-x-2 mb-12">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg ${
                currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              Previous
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-10 h-10 rounded-lg ${
                    currentPage === pageNum
                      ? "bg-blue-600 text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            {totalPages > 5 && <span className="px-2">...</span>}

            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg ${
                currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              Next
            </button>
          </div>
        )}

        {/* Recently Joined Section */}
        {recentlyJoined.length > 0 && (
          <div className="border-t border-gray-200 pt-12 mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Recently Joined Alumni
              </h2>
              <Clock className="h-5 w-5 text-gray-400" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {recentlyJoined.map((alumnus) => (
                <div
                  key={alumnus._id}
                  className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/alumni/${alumnus._id}`)}
                >
                  <div className="flex items-center space-x-3">
                    <div className="h-12 w-12 rounded-full overflow-hidden">
                      {alumnus.personalInfo?.avatar ? (
                        <img
                          src={alumnus.personalInfo.avatar}
                          alt={alumnus.firstName}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full bg-blue-50 flex items-center justify-center">
                          <User className="h-6 w-6 text-blue-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {alumnus.firstName} {alumnus.lastName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {alumnus.department}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlumniDirectory;
